import React, { useEffect, useState } from "react";
import api from "../../api/axois";
import styles from "./BookingManagement.module.css";

// Utility to get days in a month
function getDaysInMonth(year, month) {
  return new Date(year, month + 1, 0).getDate();
}

// Utility to format date as yyyy-mm-dd
function formatDate(date) {
  return date.toISOString().split("T")[0];
}

const BookingCalendarView = () => {
  const [rooms, setRooms] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [year, setYear] = useState(new Date().getFullYear());
  const [month, setMonth] = useState(new Date().getMonth());
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [tooltip, setTooltip] = useState({ show: false, content: '', x: 0, y: 0 });
  
  // Drag selection state
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState(null);
  const [dragEnd, setDragEnd] = useState(null);
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [showBookingForm, setShowBookingForm] = useState(false);
  
  // Calendar visibility state
  const [isCalendarExpanded, setIsCalendarExpanded] = useState(false);
  const [selectedRoomForPan, setSelectedRoomForPan] = useState(null);

  useEffect(() => {
    api.get("/roommanagement").then(res => {
      const roomList = res.data.rooms || res.data || [];
      setRooms(roomList);
      console.log('Rooms:', roomList);
    });
    api.get("/bookings").then(res => {
      const bookingList = res.data.bookings || [];
      setBookings(bookingList);
      console.log('Bookings:', bookingList);
    });
  }, []);

  // Add global mouse up listener for drag functionality
  useEffect(() => {
    const handleGlobalMouseUp = () => {
      if (isDragging) {
        handleMouseUp();
      }
    };

    document.addEventListener('mouseup', handleGlobalMouseUp);
    return () => {
      document.removeEventListener('mouseup', handleGlobalMouseUp);
    };
  }, [isDragging, dragStart, dragEnd, selectedRoom]);

  // Scroll to selected room when calendar expands
  useEffect(() => {
    if (isCalendarExpanded && selectedRoomForPan) {
      const timer = setTimeout(() => {
        const roomElement = document.getElementById(`room-row-${selectedRoomForPan.id}`);
        if (roomElement) {
          roomElement.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
            inline: 'nearest'
          });
          // Add a subtle highlight effect
          roomElement.style.backgroundColor = 'rgba(102, 126, 234, 0.1)';
          roomElement.style.transition = 'background-color 0.3s ease';
          setTimeout(() => {
            roomElement.style.backgroundColor = '';
          }, 2000);
        }
        setSelectedRoomForPan(null);
      }, 600); // Wait for calendar expansion animation
      return () => clearTimeout(timer);
    }
  }, [isCalendarExpanded, selectedRoomForPan]);

  const daysInMonth = getDaysInMonth(year, month);
  // Get the first day of the month (0=Sunday, 1=Monday, ...)
  const firstDayOfWeek = new Date(year, month, 1).getDay();
  // Build a 2D array: weeks[week][day] = date number or null
  const weeks = [];
  let week = Array(firstDayOfWeek).fill(null);
  for (let d = 1; d <= daysInMonth; d++) {
    week.push(d);
    if (week.length === 7) {
      weeks.push(week);
      week = [];
    }
  }
  if (week.length > 0) {
    while (week.length < 7) week.push(null);
    weeks.push(week);
  }

  // Build a map: roomId -> array of bookings for that room (exclude cancelled bookings)
  const bookingsByRoom = {};
  bookings.forEach(b => {
    // Skip cancelled bookings - they should not block room availability
    if (b.status === 'cancelled') return;
    
    // Try to match room_id, roomID, or room_type_id
    const roomId = b.room_id || b.roomID || b.room_type_id;
    if (!roomId) return;
    if (!bookingsByRoom[roomId]) bookingsByRoom[roomId] = [];
    bookingsByRoom[roomId].push(b);
  });

  // Helper: check if a booking covers a date
  function parseDate(val) {
    if (!val) return null;
    if (val instanceof Date) return val;
    // Accept YYYY-MM-DD or YYYY-MM-DDTHH:mm:ssZ
    if (/^\d{4}-\d{2}-\d{2}$/.test(val)) {
      const [y, m, d] = val.split('-').map(Number);
      return new Date(y, m - 1, d);
    }
    return new Date(val);
  }

  function isBookedOn(booking, dateStr) {
    const start = parseDate(booking.start_date || booking.startDate);
    const end = parseDate(booking.end_date || booking.endDate);
    const d = parseDate(dateStr);
    // Only compare date part
    return d && start && end && d >= new Date(start.getFullYear(), start.getMonth(), start.getDate()) &&
           d <= new Date(end.getFullYear(), end.getMonth(), end.getDate());
  }

  // Helper: get bookings for a room that overlap with the current month (exclude cancelled)
  function getBookingsForMonth(roomBookings) {
    return roomBookings.filter(b => {
      // Skip cancelled bookings
      if (b.status === 'cancelled') return false;
      
      const start = parseDate(b.start_date || b.startDate);
      const end = parseDate(b.end_date || b.endDate);
      return (
        start && end &&
        (start.getFullYear() < year || (start.getFullYear() === year && start.getMonth() <= month)) &&
        (end.getFullYear() > year || (end.getFullYear() === year && end.getMonth() >= month))
      );
    });
  }

  // Month/year navigation
  function changeMonth(delta) {
    let newMonth = month + delta;
    let newYear = year;
    if (newMonth < 0) {
      newMonth = 11;
      newYear--;
    } else if (newMonth > 11) {
      newMonth = 0;
      newYear++;
    }
    setMonth(newMonth);
    setYear(newYear);
  }

  // Tooltip and modal handlers
  const showTooltip = (e, content) => {
    setTooltip({
      show: true,
      content,
      x: e.clientX + 10,
      y: e.clientY + 10
    });
  };

  const hideTooltip = () => {
    setTooltip({ show: false, content: '', x: 0, y: 0 });
  };

  const showBookingDetails = (booking) => {
    setSelectedBooking(booking);
    setShowModal(true);
  };

  // Drag selection handlers
  const handleMouseDown = (room, day, e) => {
    e.preventDefault();
    const dateStr = formatDate(new Date(year, month, day));
    console.log('Starting drag from:', dateStr, 'Room:', room.id);
    setIsDragging(true);
    setDragStart(dateStr);
    setDragEnd(dateStr);
    setSelectedRoom(room);
    hideTooltip(); // Hide tooltip during drag
  };

  const handleMouseEnter = (room, day, e) => {
    if (isDragging && selectedRoom && selectedRoom.id === room.id) {
      const dateStr = formatDate(new Date(year, month, day));
      console.log('Dragging to:', dateStr);
      setDragEnd(dateStr);
    }
  };

  const handleMouseUp = () => {
    if (isDragging && dragStart && dragEnd && selectedRoom) {
      // Ensure dragStart is before dragEnd
      const startDate = new Date(dragStart);
      const endDate = new Date(dragEnd);
      const actualStart = startDate <= endDate ? startDate : endDate;
      const actualEnd = startDate <= endDate ? endDate : startDate;
      
      // Add one day to end date to make it a proper check-out date
      const checkOutDate = new Date(actualEnd);
      checkOutDate.setDate(checkOutDate.getDate() + 1);
      
      setDragStart(formatDate(actualStart));
      setDragEnd(formatDate(checkOutDate));
      setShowBookingForm(true);
    }
    setIsDragging(false);
  };

  const clearDragSelection = () => {
    setIsDragging(false);
    setDragStart(null);
    setDragEnd(null);
    setSelectedRoom(null);
  };

  const isDateInSelection = (room, day) => {
    if (!isDragging || !selectedRoom || selectedRoom.id !== room.id || !dragStart || !dragEnd) {
      return false;
    }
    const dateStr = formatDate(new Date(year, month, day));
    const start = new Date(dragStart);
    const end = new Date(dragEnd);
    const current = new Date(dateStr);
    const actualStart = start <= end ? start : end;
    const actualEnd = start <= end ? end : start;
    return current >= actualStart && current <= actualEnd;
  };

  return (
    <div 
      style={{ overflowX: "auto", margin: "24px 0", background: "#fff", borderRadius: 12, boxShadow: "0 4px 12px rgba(0,0,0,0.1)", border: "1px solid #e0e0e0" }}
      onMouseUp={handleMouseUp}
      onMouseLeave={() => {
        if (isDragging) {
          setIsDragging(false);
        }
      }}
    >
      <div style={{ 
        display: "flex", 
        alignItems: "center", 
        justifyContent: "space-between",
        padding: "20px 24px", 
        borderBottom: "1px solid #e0e0e0",
        background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
        borderRadius: "12px 12px 0 0",
        color: "#fff",
        cursor: "pointer"
      }}
      onClick={() => setIsCalendarExpanded(!isCalendarExpanded)}
      >
        <h3 style={{ margin: 0, fontSize: "1.5rem", fontWeight: 600 }}>
          Booking Calendar {!isCalendarExpanded && `(${rooms.length} rooms)`}
        </h3>
        <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
          {isCalendarExpanded && (
            <>
              <button 
                onClick={(e) => {
                  e.stopPropagation();
                  changeMonth(-1);
                }}
                style={{
                  background: "rgba(255,255,255,0.2)",
                  border: "1px solid rgba(255,255,255,0.3)",
                  color: "#fff",
                  padding: "8px 12px",
                  borderRadius: 8,
                  cursor: "pointer",
                  fontSize: "14px",
                  fontWeight: 500,
                  transition: "all 0.2s ease"
                }}
                onMouseOver={(e) => e.target.style.background = "rgba(255,255,255,0.3)"}
                onMouseOut={(e) => e.target.style.background = "rgba(255,255,255,0.2)"}
              >
                ‹ Previous
              </button>
              <span style={{ 
                fontSize: "1.25rem", 
                fontWeight: 600,
                padding: "0 16px",
                textShadow: "0 1px 2px rgba(0,0,0,0.1)"
              }}>
                {new Date(year, month).toLocaleString("default", { month: "long", year: "numeric" })}
              </span>
              <button 
                onClick={(e) => {
                  e.stopPropagation();
                  changeMonth(1);
                }}
                style={{
                  background: "rgba(255,255,255,0.2)",
                  border: "1px solid rgba(255,255,255,0.3)",
                  color: "#fff",
                  padding: "8px 12px",
                  borderRadius: 8,
                  cursor: "pointer",
                  fontSize: "14px",
                  fontWeight: 500,
                  transition: "all 0.2s ease"
                }}
                onMouseOver={(e) => e.target.style.background = "rgba(255,255,255,0.3)"}
                onMouseOut={(e) => e.target.style.background = "rgba(255,255,255,0.2)"}
              >
                Next ›
              </button>
            </>
          )}
          <div style={{
            fontSize: "1.5rem",
            transform: isCalendarExpanded ? "rotate(180deg)" : "rotate(0deg)",
            transition: "transform 0.3s ease"
          }}>
            ▼
          </div>
        </div>
      </div>
      <div style={{ padding: "0 24px 24px" }}>
        {/* Room List - Always visible */}
        {!isCalendarExpanded && (
          <div style={{
            marginTop: 20,
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
            gap: 16
          }}>
            {rooms.map(room => (
              <div key={room.id} style={{
                padding: "16px",
                background: "linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)",
                borderRadius: 8,
                border: "1px solid #dee2e6",
                transition: "all 0.2s ease",
                cursor: "pointer"
              }}
              onMouseOver={(e) => e.target.style.transform = "translateY(-2px)"}
              onMouseOut={(e) => e.target.style.transform = "translateY(0)"}
              onClick={() => {
                setSelectedRoomForPan(room);
                setIsCalendarExpanded(true);
              }}
              >
                <div style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  marginBottom: 8
                }}>
                  <div style={{
                    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                    color: "#fff",
                    padding: "6px 12px",
                    borderRadius: 6,
                    fontSize: "14px",
                    fontWeight: 600
                  }}>
                    {room.room_number ? `Room ${room.room_number}` : `Room ${room.id}`}
                  </div>
                  <div style={{
                    fontSize: "12px",
                    color: room.availability === 'Available' ? "#28a745" : "#dc3545",
                    fontWeight: 600
                  }}>
                    {room.availability}
                  </div>
                </div>
                <div style={{
                  fontSize: "14px",
                  color: "#495057",
                  marginBottom: 4
                }}>
                  {room.roomType || 'Standard Room'}
                </div>
                <div style={{
                  fontSize: "16px",
                  fontWeight: 600,
                  color: "#667eea"
                }}>
                  {room.cost || 'N/A'}
                </div>
              </div>
            ))}
          </div>
        )}
        
        {/* Calendar - Expandable */}
        <div style={{
          maxHeight: isCalendarExpanded ? "2000px" : "0",
          overflow: "hidden",
          transition: "max-height 0.5s ease-in-out, opacity 0.3s ease",
          opacity: isCalendarExpanded ? 1 : 0
        }}>
        <table style={{ 
          width: "100%", 
          minWidth: 1200, 
          borderCollapse: "separate", 
          borderSpacing: 0,
          marginTop: 20,
          borderRadius: 8,
          overflow: "hidden",
          border: "1px solid #e0e0e0"
        }}>
          <thead>
            <tr>
              <th style={{ 
                minWidth: 120, 
                maxWidth: 150, 
                background: "linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)",
                padding: "16px 12px",
                fontSize: "14px",
                fontWeight: 600,
                color: "#495057",
                border: "1px solid #dee2e6",
                borderRight: "2px solid #adb5bd"
              }}>
                Room
              </th>
              <th colSpan={7} style={{ 
                background: "linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)", 
                textAlign: 'center', 
                padding: "16px 12px",
                fontSize: "16px",
                fontWeight: 600,
                color: "#495057",
                border: "1px solid #dee2e6"
              }}>
                {new Date(year, month).toLocaleString("default", { month: "long", year: "numeric" })} Calendar
              </th>
            </tr>
            <tr>
              <th style={{ 
                background: "#f8f9fa",
                padding: "12px",
                border: "1px solid #dee2e6",
                borderRight: "2px solid #adb5bd"
              }}></th>
              {['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'].map((d, i) => (
                <th key={d} style={{ 
                  width: 140, 
                  background: i >= 5 ? "linear-gradient(135deg, #fff3e0 0%, #ffe0b2 100%)" : "linear-gradient(135deg, #f3e5f5 0%, #e1bee7 100%)", 
                  textAlign: 'center',
                  padding: "12px 8px",
                  fontSize: "13px",
                  fontWeight: 600,
                  color: "#495057",
                  border: "1px solid #dee2e6"
                }}>
                  <div>{d.substring(0, 3)}</div>
                  <div style={{ fontSize: "11px", fontWeight: 400, opacity: 0.7 }}>{d.substring(3)}</div>
                </th>
              ))}
            </tr>
          </thead>
        <tbody>
          {rooms.map(room => {
            const roomId = room.id;
            const roomBookings = bookingsByRoom[roomId] || [];
            // Also get cancelled bookings for display purposes (but not blocking)
            const cancelledBookings = bookings.filter(b => {
              const bookingRoomId = b.room_id || b.roomID || b.room_type_id;
              return bookingRoomId === roomId && b.status === 'cancelled';
            });
            
            // For each booking, calculate its start and end day in this month
            const bookingsForMonth = roomBookings.filter(b => {
              const start = parseDate(b.start_date || b.startDate);
              const end = parseDate(b.end_date || b.endDate);
              return (
                start && end &&
                (start.getFullYear() < year || (start.getFullYear() === year && start.getMonth() <= month)) &&
                (end.getFullYear() > year || (end.getFullYear() === year && end.getMonth() >= month))
              );
            });
            
            // Get cancelled bookings for this month for visual reference (but not blocking)
            const cancelledForMonth = cancelledBookings.filter(b => {
              const start = parseDate(b.start_date || b.startDate);
              const end = parseDate(b.end_date || b.endDate);
              return (
                start && end &&
                (start.getFullYear() < year || (start.getFullYear() === year && start.getMonth() <= month)) &&
                (end.getFullYear() > year || (end.getFullYear() === year && end.getMonth() >= month))
              );
            });
            // For each week, build a row of cells
            return weeks.map((week, weekIdx) => (
              <tr key={roomId + '-week-' + weekIdx} id={weekIdx === 0 ? `room-row-${roomId}` : undefined}>
                {weekIdx === 0 ? (
                  <td rowSpan={weeks.length} style={{ 
                    fontWeight: 600, 
                    background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)', 
                    borderRight: '2px solid #adb5bd', 
                    verticalAlign: 'middle', 
                    minWidth: 120, 
                    maxWidth: 150,
                    padding: "16px 12px",
                    fontSize: "14px",
                    color: "#495057",
                    border: "1px solid #dee2e6",
                    textAlign: "center"
                  }}>
                    <div style={{ 
                      background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                      color: "#fff",
                      padding: "8px 12px",
                      borderRadius: 8,
                      fontSize: "13px",
                      fontWeight: 600,
                      boxShadow: "0 2px 4px rgba(0,0,0,0.1)"
                    }}>
                      {room.room_number ? (
                        <div>
                          <div>Room {room.room_number}</div>
                          {room.roomType && <div style={{ fontSize: "11px", opacity: 0.9, marginTop: "2px" }}>{room.roomType}</div>}
                        </div>
                      ) : (room.title || room.name || `Room ${roomId}`)}
                    </div>
                  </td>
                ) : null}
                {week.map((day, dayIdx) => {
                  if (!day) return (
                    <td key={dayIdx} style={{ 
                      background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
                      border: "1px solid #dee2e6",
                      height: 60
                    }}></td>
                  );
                  // Find if this day is the start of a booking
                  const booking = bookingsForMonth.find(b => {
                    const start = parseDate(b.start_date || b.startDate);
                    return start && start.getFullYear() === year && start.getMonth() === month && start.getDate() === day;
                  });
                  
                  // Check for cancelled bookings on this day (for visual reference only)
                  const cancelledBooking = cancelledForMonth.find(b => {
                    const start = parseDate(b.start_date || b.startDate);
                    const end = parseDate(b.end_date || b.endDate);
                    return start && end && new Date(year, month, day) >= new Date(start.getFullYear(), start.getMonth(), start.getDate()) && new Date(year, month, day) <= new Date(end.getFullYear(), end.getMonth(), end.getDate());
                  });
                  
                  // Or if this day is inside a booking
                  const bookingCover = bookingsForMonth.find(b => {
                    const start = parseDate(b.start_date || b.startDate);
                    const end = parseDate(b.end_date || b.endDate);
                    return start && end && new Date(year, month, day) >= new Date(start.getFullYear(), start.getMonth(), start.getDate()) && new Date(year, month, day) <= new Date(end.getFullYear(), end.getMonth(), end.getDate());
                  });
                  // If this is the start of a booking, calculate how many days it spans in this week
                  if (booking) {
                    const start = parseDate(booking.start_date || booking.startDate);
                    const end = parseDate(booking.end_date || booking.endDate);
                    let span = 1;
                    for (let d = dayIdx + 1; d < 7; d++) {
                      const nextDay = week[d];
                      if (!nextDay) break;
                      if (end && new Date(year, month, nextDay) <= new Date(end.getFullYear(), end.getMonth(), end.getDate())) {
                        span++;
                      } else {
                        break;
                      }
                    }
                    return (
                      <td key={dayIdx} colSpan={span} 
                        style={{ 
                          padding: 0, 
                          border: '1px solid #dee2e6', 
                          background: 'linear-gradient(135deg, #28a745 0%, #20c997 100%)', 
                          color: '#fff', 
                          position: 'relative', 
                          borderRadius: 0,
                          height: 60,
                          boxShadow: "inset 0 1px 3px rgba(0,0,0,0.1)",
                          cursor: 'pointer',
                          transition: 'all 0.2s ease'
                        }}
                        onClick={() => showBookingDetails(booking)}
                        onMouseEnter={(e) => showTooltip(e, `${booking.guest_name || booking.guest_email || 'Guest'} - Click for details`)}
                        onMouseLeave={hideTooltip}
                        onMouseOver={(e) => e.currentTarget.style.transform = 'scale(1.02)'}
                        onMouseOut={(e) => e.currentTarget.style.transform = 'scale(1)'}
                      >
                        <div style={{ 
                          padding: '8px 10px', 
                          whiteSpace: 'nowrap', 
                          overflow: 'hidden', 
                          textOverflow: 'ellipsis', 
                          fontSize: 12,
                          height: "100%",
                          display: "flex",
                          flexDirection: "column",
                          justifyContent: "center"
                        }}>
                          <div style={{ 
                            display: "flex", 
                            alignItems: "center", 
                            marginBottom: 4 
                          }}>
                            <span style={{ 
                              fontSize: 14, 
                              fontWeight: 700, 
                              marginRight: 8,
                              background: "rgba(255,255,255,0.2)",
                              padding: "2px 6px",
                              borderRadius: 4,
                              minWidth: 20,
                              textAlign: "center"
                            }}>
                              {day}
                            </span>
                            <span style={{ 
                              fontSize: 11, 
                              fontWeight: 600,
                              textShadow: "0 1px 2px rgba(0,0,0,0.1)"
                            }}>
                              ● BOOKED
                            </span>
                          </div>
                          <div style={{ 
                            fontSize: 11, 
                            fontWeight: 500, 
                            opacity: 0.95,
                            textShadow: "0 1px 2px rgba(0,0,0,0.1)"
                          }}>
                            {booking.guest_name || booking.guest_email || 'Guest'}
                          </div>
                          <div style={{ 
                            fontSize: 10, 
                            fontWeight: 400, 
                            opacity: 0.8,
                            marginTop: 2,
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center'
                          }}>
                            <span>{(booking.start_date || booking.startDate)?.split('T')[0]} - {(booking.end_date || booking.endDate)?.split('T')[0]}</span>
                            {booking.status && booking.status !== 'confirmed' && (
                              <span style={{
                                fontSize: 8,
                                fontWeight: 700,
                                background: 'rgba(255,255,255,0.9)',
                                color: '#333',
                                padding: '1px 4px',
                                borderRadius: 2,
                                textTransform: 'uppercase'
                              }}>
                                {booking.status}
                              </span>
                            )}
                          </div>
                        </div>
                      </td>
                    );
                  }
                  // If this day is inside a booking but not the start, skip rendering (merged by colSpan)
                  if (bookingCover) {
                    return null;
                  }
                  // Otherwise, empty cell available for booking
                  const isWeekend = dayIdx === 0 || dayIdx === 6;
                  const isSelected = isDateInSelection(room, day);
                  
                  return (
                    <td key={dayIdx} 
                      style={{ 
                        background: isSelected 
                          ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                          : isWeekend 
                          ? 'linear-gradient(135deg, #fff8e1 0%, #ffecb3 100%)' 
                          : '#fff', 
                        border: '1px solid #dee2e6', 
                        position: 'relative',
                        height: 60,
                        transition: "all 0.2s ease",
                        cursor: 'crosshair',
                        userSelect: 'none'
                      }}
                      onMouseDown={(e) => handleMouseDown(room, day, e)}
                      onMouseEnter={(e) => {
                        handleMouseEnter(room, day, e);
                        if (!isDragging) {
                          if (!isSelected) {
                            e.currentTarget.style.background = isWeekend ? '#fff3e0' : '#f8f9fa';
                          }
                          showTooltip(e, `${new Date(year, month, day).toLocaleDateString()} - Click and drag to book`);
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!isDragging && !isSelected) {
                          e.currentTarget.style.background = isWeekend ? 'linear-gradient(135deg, #fff8e1 0%, #ffecb3 100%)' : '#fff';
                        }
                        if (!isDragging) {
                          hideTooltip();
                        }
                      }}
                    >
                      <div style={{ 
                        position: 'absolute', 
                        top: 4, 
                        left: 6, 
                        fontSize: 16, 
                        fontWeight: 600,
                        color: isSelected ? '#fff' : isWeekend ? '#f57c00' : '#495057'
                      }}>
                        {day}
                      </div>
                      {isSelected && (
                        <div style={{
                          position: 'absolute',
                          bottom: 4,
                          left: 6,
                          fontSize: 10,
                          fontWeight: 500,
                          color: '#fff',
                          opacity: 0.9
                        }}>
                          Selected
                        </div>
                      )}
                      {/* Show cancelled booking as subtle overlay */}
                      {cancelledBooking && (
                        <div style={{
                          position: 'absolute',
                          top: 20,
                          left: 6,
                          right: 6,
                          fontSize: 8,
                          fontWeight: 500,
                          color: '#dc3545',
                          background: 'rgba(220, 53, 69, 0.1)',
                          border: '1px solid rgba(220, 53, 69, 0.3)',
                          borderRadius: 3,
                          padding: '2px 4px',
                          textAlign: 'center',
                          opacity: 0.8
                        }}>
                          CANCELLED
                        </div>
                      )}
                    </td>
                  );
                })}
              </tr>
            ));
          })}
        </tbody>
      </table>
      <div style={{ 
        marginTop: 20, 
        display: "flex", 
        alignItems: "center", 
        justifyContent: "space-between",
        padding: "16px 20px",
        background: "linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)",
        borderRadius: 8,
        border: "1px solid #dee2e6"
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <div style={{ 
              width: 16, 
              height: 16, 
              background: "linear-gradient(135deg, #28a745 0%, #20c997 100%)", 
              borderRadius: 4,
              boxShadow: "0 1px 3px rgba(0,0,0,0.1)"
            }}></div>
            <span style={{ fontSize: 14, fontWeight: 500, color: "#495057" }}>Booked</span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <div style={{ 
              width: 16, 
              height: 16, 
              background: "rgba(220, 53, 69, 0.1)", 
              border: "1px solid rgba(220, 53, 69, 0.3)",
              borderRadius: 4
            }}></div>
            <span style={{ fontSize: 14, fontWeight: 500, color: "#495057" }}>Cancelled</span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <div style={{ 
              width: 16, 
              height: 16, 
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)", 
              borderRadius: 4,
              boxShadow: "0 1px 3px rgba(0,0,0,0.1)"
            }}></div>
            <span style={{ fontSize: 14, fontWeight: 500, color: "#495057" }}>Selected</span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <div style={{ 
              width: 16, 
              height: 16, 
              background: "linear-gradient(135deg, #fff8e1 0%, #ffecb3 100%)", 
              borderRadius: 4,
              border: "1px solid #f57c00"
            }}></div>
            <span style={{ fontSize: 14, fontWeight: 500, color: "#495057" }}>Weekend</span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <div style={{ 
              width: 16, 
              height: 16, 
              background: "#fff", 
              borderRadius: 4,
              border: "1px solid #dee2e6"
            }}></div>
            <span style={{ fontSize: 14, fontWeight: 500, color: "#495057" }}>Available</span>
          </div>
        </div>
        <div style={{ 
          fontSize: 13, 
          color: "#6c757d",
          fontStyle: "italic"
        }}>
          Click and drag across dates to book • Hover for details • Click booked dates for info
        </div>
      </div>
      </div>
    </div>
    
    {/* Tooltip */}
    {tooltip.show && (
      <div style={{
        position: 'fixed',
        top: tooltip.y,
        left: tooltip.x,
        background: 'rgba(0,0,0,0.9)',
        color: '#fff',
        padding: '8px 12px',
        borderRadius: 6,
        fontSize: 12,
        fontWeight: 500,
        zIndex: 1000,
        pointerEvents: 'none',
        maxWidth: 200,
        boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
        border: '1px solid rgba(255,255,255,0.1)'
      }}>
        {tooltip.content}
      </div>
    )}

    {/* Booking Details Modal */}
    {showModal && selectedBooking && (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0,0,0,0.6)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1001,
        backdropFilter: 'blur(4px)'
      }} onClick={() => setShowModal(false)}>
        <div style={{
          background: '#fff',
          borderRadius: 16,
          padding: '32px',
          maxWidth: 500,
          width: '90%',
          maxHeight: '80vh',
          overflowY: 'auto',
          boxShadow: '0 20px 40px rgba(0,0,0,0.3)',
          border: '1px solid #e0e0e0'
        }} onClick={e => e.stopPropagation()}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: 24,
            paddingBottom: 16,
            borderBottom: '1px solid #e0e0e0'
          }}>
            <h2 style={{
              margin: 0,
              fontSize: '1.5rem',
              fontWeight: 600,
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text'
            }}>
              Booking Details
            </h2>
            <button
              onClick={() => setShowModal(false)}
              style={{
                background: 'none',
                border: 'none',
                fontSize: '24px',
                cursor: 'pointer',
                color: '#666',
                padding: '4px 8px',
                borderRadius: 4,
                transition: 'color 0.2s ease'
              }}
              onMouseOver={(e) => e.target.style.color = '#333'}
              onMouseOut={(e) => e.target.style.color = '#666'}
            >
              ×
            </button>
          </div>
          
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr 1fr',
              gap: 16,
              marginBottom: 16
            }}>
              <div style={{
                padding: '16px',
                background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
                borderRadius: 8,
                border: '1px solid #dee2e6'
              }}>
                <div style={{ fontSize: 12, color: '#666', marginBottom: 4, fontWeight: 500 }}>Guest Name</div>
                <div style={{ fontSize: 16, fontWeight: 600, color: '#333' }}>
                  {selectedBooking.guest_name || 'N/A'}
                </div>
              </div>
              <div style={{
                padding: '16px',
                background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
                borderRadius: 8,
                border: '1px solid #dee2e6'
              }}>
                <div style={{ fontSize: 12, color: '#666', marginBottom: 4, fontWeight: 500 }}>Guest Email</div>
                <div style={{ fontSize: 16, fontWeight: 600, color: '#333' }}>
                  {selectedBooking.guest_email || 'N/A'}
                </div>
              </div>
              <div style={{
                padding: '16px',
                background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
                borderRadius: 8,
                border: '1px solid #dee2e6'
              }}>
                <div style={{ fontSize: 12, color: '#666', marginBottom: 4, fontWeight: 500 }}>Guest Phone</div>
                <div style={{ fontSize: 16, fontWeight: 600, color: '#333' }}>
                  {selectedBooking.guest_phone || 'N/A'}
                </div>
              </div>
            </div>
            
            <div style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: 16,
              marginBottom: 16
            }}>
              <div style={{
                padding: '16px',
                background: 'linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%)',
                borderRadius: 8,
                border: '1px solid #a5d6a7'
              }}>
                <div style={{ fontSize: 12, color: '#2e7d32', marginBottom: 4, fontWeight: 500 }}>Check-in Date</div>
                <div style={{ fontSize: 16, fontWeight: 600, color: '#1b5e20' }}>
                  {new Date(selectedBooking.start_date).toLocaleDateString('en-US', {
                    weekday: 'short',
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric'
                  })}
                </div>
              </div>
              <div style={{
                padding: '16px',
                background: 'linear-gradient(135deg, #fff3e0 0%, #ffe0b2 100%)',
                borderRadius: 8,
                border: '1px solid #ffcc02'
              }}>
                <div style={{ fontSize: 12, color: '#f57c00', marginBottom: 4, fontWeight: 500 }}>Check-out Date</div>
                <div style={{ fontSize: 16, fontWeight: 600, color: '#ef6c00' }}>
                  {new Date(selectedBooking.end_date).toLocaleDateString('en-US', {
                    weekday: 'short',
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric'
                  })}
                </div>
              </div>
            </div>
            
            <div style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: 16
            }}>
              <div style={{
                padding: '16px',
                background: 'linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)',
                borderRadius: 8,
                border: '1px solid #90caf9'
              }}>
                <div style={{ fontSize: 12, color: '#1976d2', marginBottom: 4, fontWeight: 500 }}>Room Type</div>
                <div style={{ fontSize: 16, fontWeight: 600, color: '#0d47a1' }}>
                  {selectedBooking.roomType || 'N/A'}
                </div>
              </div>
              <div style={{
                padding: '16px',
                background: 'linear-gradient(135deg, #f3e5f5 0%, #e1bee7 100%)',
                borderRadius: 8,
                border: '1px solid #ce93d8'
              }}>
                <div style={{ fontSize: 12, color: '#7b1fa2', marginBottom: 4, fontWeight: 500 }}>Total Cost</div>
                <div style={{ fontSize: 16, fontWeight: 600, color: '#4a148c' }}>
                  {selectedBooking.cost || selectedBooking.total_cost || 'N/A'}
                </div>
              </div>
            </div>
            
            {/* Breakfast Information */}
            {(selectedBooking.breakfast || selectedBooking.breakfast_count) && (
              <div style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: 16,
                marginBottom: 16
              }}>
                <div style={{
                  padding: '16px',
                  background: 'linear-gradient(135deg, #fff8e1 0%, #ffecb3 100%)',
                  borderRadius: 8,
                  border: '1px solid #ffcc02'
                }}>
                  <div style={{ fontSize: 12, color: '#f57c00', marginBottom: 4, fontWeight: 500 }}>Breakfast Included</div>
                  <div style={{ fontSize: 16, fontWeight: 600, color: '#ef6c00' }}>
                    {selectedBooking.breakfast ? 'Yes' : 'No'}
                  </div>
                </div>
                {selectedBooking.breakfast_count && (
                  <div style={{
                    padding: '16px',
                    background: 'linear-gradient(135deg, #fff8e1 0%, #ffecb3 100%)',
                    borderRadius: 8,
                    border: '1px solid #ffcc02'
                  }}>
                    <div style={{ fontSize: 12, color: '#f57c00', marginBottom: 4, fontWeight: 500 }}>People Count</div>
                    <div style={{ fontSize: 16, fontWeight: 600, color: '#ef6c00' }}>
                      {selectedBooking.breakfast_count} {selectedBooking.breakfast_count === 1 ? 'person' : 'people'}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Airport Conveyance Information */}
            {selectedBooking.airport_conveyance && (
              <div style={{
                marginBottom: 16,
                padding: '20px',
                background: 'linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%)',
                borderRadius: 8,
                border: '1px solid #a5d6a7'
              }}>
                <div style={{ fontSize: 14, color: '#2e7d32', marginBottom: 12, fontWeight: 600 }}>Airport Conveyance Details</div>
                
                {/* Service Type */}
                <div style={{ marginBottom: 16 }}>
                  <div style={{ fontSize: 12, color: '#2e7d32', marginBottom: 4, fontWeight: 500 }}>Service Type</div>
                  <div style={{ fontSize: 14, fontWeight: 600, color: '#1b5e20', textTransform: 'capitalize' }}>
                    {selectedBooking.airport_conveyance.direction}
                  </div>
                </div>

                {/* Pickup Details */}
                {selectedBooking.airport_conveyance.pickup && (
                  <div style={{ marginBottom: 16, padding: '12px', background: 'rgba(255,255,255,0.5)', borderRadius: 6 }}>
                    <div style={{ fontSize: 12, color: '#2e7d32', marginBottom: 8, fontWeight: 600 }}>✈️ Pickup Details</div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, fontSize: 13 }}>
                      {selectedBooking.airport_conveyance.pickup.flight_number && (
                        <div>
                          <span style={{ color: '#666', fontWeight: 500 }}>Flight: </span>
                          <span style={{ fontWeight: 600 }}>{selectedBooking.airport_conveyance.pickup.flight_number}</span>
                        </div>
                      )}
                      {selectedBooking.airport_conveyance.pickup.airline && (
                        <div>
                          <span style={{ color: '#666', fontWeight: 500 }}>Airline: </span>
                          <span style={{ fontWeight: 600 }}>{selectedBooking.airport_conveyance.pickup.airline}</span>
                        </div>
                      )}
                      {selectedBooking.airport_conveyance.pickup.arrival_time && (
                        <div>
                          <span style={{ color: '#666', fontWeight: 500 }}>Arrival: </span>
                          <span style={{ fontWeight: 600 }}>{new Date(selectedBooking.airport_conveyance.pickup.arrival_time).toLocaleString()}</span>
                        </div>
                      )}
                      {selectedBooking.airport_conveyance.pickup.terminal && (
                        <div>
                          <span style={{ color: '#666', fontWeight: 500 }}>Terminal: </span>
                          <span style={{ fontWeight: 600 }}>{selectedBooking.airport_conveyance.pickup.terminal}</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Drop-off Details */}
                {selectedBooking.airport_conveyance.dropoff && (
                  <div style={{ padding: '12px', background: 'rgba(255,255,255,0.5)', borderRadius: 6 }}>
                    <div style={{ fontSize: 12, color: '#2e7d32', marginBottom: 8, fontWeight: 600 }}>🚗 Drop-off Details</div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, fontSize: 13 }}>
                      {selectedBooking.airport_conveyance.dropoff.flight_number && (
                        <div>
                          <span style={{ color: '#666', fontWeight: 500 }}>Flight: </span>
                          <span style={{ fontWeight: 600 }}>{selectedBooking.airport_conveyance.dropoff.flight_number}</span>
                        </div>
                      )}
                      {selectedBooking.airport_conveyance.dropoff.airline && (
                        <div>
                          <span style={{ color: '#666', fontWeight: 500 }}>Airline: </span>
                          <span style={{ fontWeight: 600 }}>{selectedBooking.airport_conveyance.dropoff.airline}</span>
                        </div>
                      )}
                      {selectedBooking.airport_conveyance.dropoff.departure_time && (
                        <div>
                          <span style={{ color: '#666', fontWeight: 500 }}>Departure: </span>
                          <span style={{ fontWeight: 600 }}>{new Date(selectedBooking.airport_conveyance.dropoff.departure_time).toLocaleString()}</span>
                        </div>
                      )}
                      {selectedBooking.airport_conveyance.dropoff.terminal && (
                        <div>
                          <span style={{ color: '#666', fontWeight: 500 }}>Terminal: </span>
                          <span style={{ fontWeight: 600 }}>{selectedBooking.airport_conveyance.dropoff.terminal}</span>
                        </div>
                      )}
                      {selectedBooking.airport_conveyance.dropoff.pickup_time && (
                        <div style={{ gridColumn: '1 / -1' }}>
                          <span style={{ color: '#666', fontWeight: 500 }}>Hotel Pickup: </span>
                          <span style={{ fontWeight: 600 }}>{new Date(selectedBooking.airport_conveyance.dropoff.pickup_time).toLocaleString()}</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Affiliate Information */}
            {selectedBooking.affiliate_code && (
              <div style={{
                marginBottom: 16,
                padding: '16px',
                background: 'linear-gradient(135deg, #f3e5f5 0%, #e1bee7 100%)',
                borderRadius: 8,
                border: '1px solid #ce93d8'
              }}>
                <div style={{ fontSize: 12, color: '#7b1fa2', marginBottom: 4, fontWeight: 500 }}>Affiliate Code</div>
                <div style={{ fontSize: 16, fontWeight: 600, color: '#4a148c' }}>
                  {selectedBooking.affiliate_code}
                </div>
              </div>
            )}
            
            <div style={{
              marginTop: 20,
              padding: '16px',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              borderRadius: 8,
              textAlign: 'center'
            }}>
              <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.8)', marginBottom: 4, fontWeight: 500 }}>Booking Status</div>
              <div style={{
                fontSize: 18,
                fontWeight: 700,
                color: '#fff',
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}>
                {selectedBooking.status || 'CONFIRMED'}
              </div>
            </div>
          </div>
        </div>
      </div>
    )}
    
    {/* New Booking Form Modal */}
    {showBookingForm && selectedRoom && dragStart && dragEnd && (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0,0,0,0.6)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1002,
        backdropFilter: 'blur(4px)'
      }} onClick={() => {
        setShowBookingForm(false);
        clearDragSelection();
      }}>
        <div style={{
          background: '#fff',
          borderRadius: 16,
          padding: '32px',
          maxWidth: 600,
          width: '90%',
          maxHeight: '80vh',
          overflowY: 'auto',
          boxShadow: '0 20px 40px rgba(0,0,0,0.3)',
          border: '1px solid #e0e0e0'
        }} onClick={e => e.stopPropagation()}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: 24,
            paddingBottom: 16,
            borderBottom: '1px solid #e0e0e0'
          }}>
            <h2 style={{
              margin: 0,
              fontSize: '1.5rem',
              fontWeight: 600,
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text'
            }}>
              Create New Booking
            </h2>
            <button
              onClick={() => {
                setShowBookingForm(false);
                clearDragSelection();
              }}
              style={{
                background: 'none',
                border: 'none',
                fontSize: '24px',
                cursor: 'pointer',
                color: '#666',
                padding: '4px 8px',
                borderRadius: 4,
                transition: 'color 0.2s ease'
              }}
              onMouseOver={(e) => e.target.style.color = '#333'}
              onMouseOut={(e) => e.target.style.color = '#666'}
            >
              ×
            </button>
          </div>
          
          <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
            {/* Room and Date Info */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr 1fr',
              gap: 16,
              marginBottom: 16
            }}>
              <div style={{
                padding: '16px',
                background: 'linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)',
                borderRadius: 8,
                border: '1px solid #90caf9'
              }}>
                <div style={{ fontSize: 12, color: '#1976d2', marginBottom: 4, fontWeight: 500 }}>Room</div>
                <div style={{ fontSize: 16, fontWeight: 600, color: '#0d47a1' }}>
                  {selectedRoom.room_number ? (
                    <div>
                      <div>Room {selectedRoom.room_number}</div>
                      {selectedRoom.roomType && <div style={{ fontSize: "12px", opacity: 0.8, marginTop: "2px" }}>{selectedRoom.roomType}</div>}
                    </div>
                  ) : (selectedRoom.title || selectedRoom.name || `Room ${selectedRoom.id}`)}
                </div>
              </div>
              <div style={{
                padding: '16px',
                background: 'linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%)',
                borderRadius: 8,
                border: '1px solid #a5d6a7'
              }}>
                <div style={{ fontSize: 12, color: '#2e7d32', marginBottom: 4, fontWeight: 500 }}>Check-in</div>
                <div style={{ fontSize: 14, fontWeight: 600, color: '#1b5e20' }}>
                  {new Date(dragStart).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                </div>
              </div>
              <div style={{
                padding: '16px',
                background: 'linear-gradient(135deg, #fff3e0 0%, #ffe0b2 100%)',
                borderRadius: 8,
                border: '1px solid #ffcc02'
              }}>
                <div style={{ fontSize: 12, color: '#f57c00', marginBottom: 4, fontWeight: 500 }}>Check-out</div>
                <div style={{ fontSize: 14, fontWeight: 600, color: '#ef6c00' }}>
                  {new Date(dragEnd).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                </div>
                <div style={{ fontSize: 10, color: '#f57c00', opacity: 0.8, marginTop: 2 }}>
                  (Next day after selection)
                </div>
              </div>
            </div>

            {/* Booking Form */}
            <form onSubmit={(e) => {
              e.preventDefault();
              const formData = new FormData(e.target);
              
              // Build airport conveyance object
              const airportDirection = formData.get('airport_direction');
              const airportConveyance = airportDirection && airportDirection !== 'none' ? {
                direction: airportDirection,
                pickup: airportDirection === 'pickup' || airportDirection === 'both' ? {
                  flight_number: formData.get('pickup_flight_number'),
                  arrival_time: formData.get('pickup_arrival_time'),
                  airline: formData.get('pickup_airline'),
                  terminal: formData.get('pickup_terminal'),
                } : null,
                dropoff: airportDirection === 'dropoff' || airportDirection === 'both' ? {
                  flight_number: formData.get('dropoff_flight_number'),
                  departure_time: formData.get('dropoff_departure_time'),
                  airline: formData.get('dropoff_airline'),
                  terminal: formData.get('dropoff_terminal'),
                  pickup_time: formData.get('dropoff_pickup_time'),
                } : null
              } : null;
              
              const bookingData = {
                room_id: selectedRoom.id,
                start_date: dragStart,
                end_date: dragEnd,
                guest_name: formData.get('guest_name'),
                guest_email: formData.get('guest_email'),
                guest_phone: formData.get('guest_phone'),
                breakfast: formData.get('breakfast') === 'on',
                breakfast_count: parseInt(formData.get('breakfast_count')) || 0,
                airport_conveyance: airportConveyance,
                affiliate_code: formData.get('affiliate_code') || null,
              };
              console.log('New booking data:', bookingData);
              
              // Send to manual booking endpoint
              api.post('/bookings/manual', bookingData).then(response => {
                console.log('Booking created:', response.data);
                alert('Booking created successfully!');
                // Refresh bookings list
                api.get("/bookings").then(res => {
                  const bookingList = res.data.bookings || [];
                  setBookings(bookingList);
                });
                setShowBookingForm(false);
                clearDragSelection();
              }).catch(error => {
                console.error('Booking creation failed:', error);
                alert('Failed to create booking: ' + (error.response?.data?.message || error.message));
              });
            }}>
              <div style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: 16,
                marginBottom: 16
              }}>
                <div>
                  <label style={{ display: 'block', marginBottom: 8, fontSize: 14, fontWeight: 500, color: '#495057' }}>
                    Guest Name *
                  </label>
                  <input
                    type="text"
                    name="guest_name"
                    required
                    style={{
                      width: '100%',
                      padding: '12px',
                      border: '1px solid #dee2e6',
                      borderRadius: 8,
                      fontSize: 14,
                      fontFamily: 'inherit'
                    }}
                    placeholder="Enter guest name"
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: 8, fontSize: 14, fontWeight: 500, color: '#495057' }}>
                    Guest Email *
                  </label>
                  <input
                    type="email"
                    name="guest_email"
                    required
                    style={{
                      width: '100%',
                      padding: '12px',
                      border: '1px solid #dee2e6',
                      borderRadius: 8,
                      fontSize: 14,
                      fontFamily: 'inherit'
                    }}
                    placeholder="Enter guest email"
                  />
                </div>
              </div>
              
              <div style={{ marginBottom: 24 }}>
                <label style={{ display: 'block', marginBottom: 8, fontSize: 14, fontWeight: 500, color: '#495057' }}>
                  Guest Phone *
                </label>
                <input
                  type="tel"
                  name="guest_phone"
                  required
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '1px solid #dee2e6',
                    borderRadius: 8,
                    fontSize: 14,
                    fontFamily: 'inherit'
                  }}
                  placeholder="Enter guest phone number"
                />
              </div>

              {/* Airport Conveyance Section */}
              <div style={{ marginBottom: 24, padding: '20px', background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)', borderRadius: 8, border: '1px solid #dee2e6' }}>
                <h4 style={{ margin: '0 0 16px 0', fontSize: 16, fontWeight: 600, color: '#495057' }}>Airport Conveyance (Optional)</h4>
                <div style={{ marginBottom: 16 }}>
                  <label style={{ display: 'block', marginBottom: 8, fontSize: 14, fontWeight: 500, color: '#495057' }}>
                    Service Type
                  </label>
                  <select
                    name="airport_direction"
                    style={{
                      width: '100%',
                      padding: '12px',
                      border: '1px solid #dee2e6',
                      borderRadius: 8,
                      fontSize: 14,
                      fontFamily: 'inherit'
                    }}
                    onChange={(e) => {
                      const airportFields = document.querySelectorAll('.airport-fields');
                      const pickupFields = document.querySelectorAll('.pickup-fields');
                      const dropoffFields = document.querySelectorAll('.dropoff-fields');
                      
                      if (e.target.value === 'none') {
                        airportFields.forEach(field => field.style.display = 'none');
                      } else {
                        airportFields.forEach(field => field.style.display = 'block');
                        
                        if (e.target.value === 'pickup' || e.target.value === 'both') {
                          pickupFields.forEach(field => field.style.display = 'block');
                        } else {
                          pickupFields.forEach(field => field.style.display = 'none');
                        }
                        
                        if (e.target.value === 'dropoff' || e.target.value === 'both') {
                          dropoffFields.forEach(field => field.style.display = 'block');
                        } else {
                          dropoffFields.forEach(field => field.style.display = 'none');
                        }
                      }
                    }}
                  >
                    <option value="none">No airport service</option>
                    <option value="pickup">Pickup only (₦40,000)</option>
                    <option value="dropoff">Drop-off only (₦40,000)</option>
                    <option value="both">Both pickup & drop-off (₦70,000)</option>
                  </select>
                </div>

                {/* Pickup Fields */}
                <div className="airport-fields pickup-fields" style={{ display: 'none', marginBottom: 16 }}>
                  <h5 style={{ margin: '0 0 12px 0', fontSize: 14, fontWeight: 600, color: '#28a745' }}>Pickup Details</h5>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
                    <input name="pickup_flight_number" placeholder="Flight Number" style={{ padding: '10px', border: '1px solid #dee2e6', borderRadius: 6, fontSize: 13 }} />
                    <input name="pickup_airline" placeholder="Airline" style={{ padding: '10px', border: '1px solid #dee2e6', borderRadius: 6, fontSize: 13 }} />
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                    <input name="pickup_arrival_time" type="datetime-local" placeholder="Arrival Time" style={{ padding: '10px', border: '1px solid #dee2e6', borderRadius: 6, fontSize: 13 }} />
                    <input name="pickup_terminal" placeholder="Terminal" style={{ padding: '10px', border: '1px solid #dee2e6', borderRadius: 6, fontSize: 13 }} />
                  </div>
                </div>

                {/* Drop-off Fields */}
                <div className="airport-fields dropoff-fields" style={{ display: 'none' }}>
                  <h5 style={{ margin: '0 0 12px 0', fontSize: 14, fontWeight: 600, color: '#ff6b35' }}>Drop-off Details</h5>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
                    <input name="dropoff_flight_number" placeholder="Flight Number" style={{ padding: '10px', border: '1px solid #dee2e6', borderRadius: 6, fontSize: 13 }} />
                    <input name="dropoff_airline" placeholder="Airline" style={{ padding: '10px', border: '1px solid #dee2e6', borderRadius: 6, fontSize: 13 }} />
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
                    <input name="dropoff_departure_time" type="datetime-local" placeholder="Departure Time" style={{ padding: '10px', border: '1px solid #dee2e6', borderRadius: 6, fontSize: 13 }} />
                    <input name="dropoff_terminal" placeholder="Terminal" style={{ padding: '10px', border: '1px solid #dee2e6', borderRadius: 6, fontSize: 13 }} />
                    <input name="dropoff_pickup_time" type="datetime-local" placeholder="Hotel Pickup Time" style={{ padding: '10px', border: '1px solid #dee2e6', borderRadius: 6, fontSize: 13 }} />
                  </div>
                </div>
              </div>

              {/* Breakfast Section */}
              <div style={{ marginBottom: 24, padding: '20px', background: 'linear-gradient(135deg, #fff8e1 0%, #ffecb3 100%)', borderRadius: 8, border: '1px solid #ffcc02' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
                  <input
                    type="checkbox"
                    name="breakfast"
                    id="breakfast"
                    style={{ transform: 'scale(1.2)' }}
                    onChange={(e) => {
                      const countContainer = document.getElementById('breakfast_count_container');
                      countContainer.style.display = e.target.checked ? 'block' : 'none';
                      if (!e.target.checked) {
                        const countField = document.getElementById('breakfast_count');
                        countField.value = '';
                      }
                    }}
                  />
                  <label htmlFor="breakfast" style={{ fontSize: 16, fontWeight: 600, color: '#f57c00' }}>
                    Include Breakfast (₦6,000 per person per night)
                  </label>
                </div>
                <div id="breakfast_count_container" style={{ display: 'none' }}>
                  <label style={{ display: 'block', marginBottom: 8, fontSize: 14, fontWeight: 500, color: '#f57c00' }}>
                    Number of People
                  </label>
                  <select
                    name="breakfast_count"
                    id="breakfast_count"
                    style={{
                      width: '150px',
                      padding: '10px',
                      border: '1px solid #ffcc02',
                      borderRadius: 6,
                      fontSize: 14
                    }}
                  >
                    <option value="1">1 person</option>
                    <option value="2">2 people</option>
                    <option value="3">3 people</option>
                    <option value="4">4 people</option>
                  </select>
                </div>
              </div>

              {/* Affiliate Code */}
              <div style={{ marginBottom: 24 }}>
                <label style={{ display: 'block', marginBottom: 8, fontSize: 14, fontWeight: 500, color: '#495057' }}>
                  Affiliate Code (Optional)
                </label>
                <input
                  type="text"
                  name="affiliate_code"
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '1px solid #dee2e6',
                    borderRadius: 8,
                    fontSize: 14,
                    fontFamily: 'inherit'
                  }}
                  placeholder="Enter affiliate code if applicable"
                />
              </div>

              <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  onClick={() => {
                    setShowBookingForm(false);
                    clearDragSelection();
                  }}
                  style={{
                    padding: '12px 24px',
                    border: '1px solid #dee2e6',
                    background: '#fff',
                    color: '#495057',
                    borderRadius: 8,
                    cursor: 'pointer',
                    fontSize: 14,
                    fontWeight: 500,
                    transition: 'all 0.2s ease'
                  }}
                  onMouseOver={(e) => e.target.style.background = '#f8f9fa'}
                  onMouseOut={(e) => e.target.style.background = '#fff'}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  style={{
                    padding: '12px 24px',
                    border: 'none',
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    color: '#fff',
                    borderRadius: 8,
                    cursor: 'pointer',
                    fontSize: 14,
                    fontWeight: 600,
                    transition: 'all 0.2s ease',
                    boxShadow: '0 2px 4px rgba(102, 126, 234, 0.3)'
                  }}
                  onMouseOver={(e) => e.target.style.transform = 'translateY(-1px)'}
                  onMouseOut={(e) => e.target.style.transform = 'translateY(0)'}
                >
                  Create Booking
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    )}
    </div>
  );
};

export default BookingCalendarView;
